import { useEffect, useRef } from "react";
import { useNavigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, FileText, UserCheck, Settings, LogOut, Printer, Inbox, ListChecks, UserCircle2, Truck } from "lucide-react";
import { getUser, clearAuth, ROLE_LABEL } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-item-dashboard" },
  { to: "/anfragen", label: "Anfragen", icon: Inbox, testid: "nav-item-anfragen" },
  { to: "/kunden", label: "Kunden", icon: Users, testid: "nav-item-kunden" },
  { to: "/auftraege", label: "Aufträge", icon: FileText, testid: "nav-item-auftraege" },
  { to: "/versand", label: "Versand", icon: Truck, testid: "nav-item-versand" },
  { to: "/aufgaben", label: "Aufgaben", icon: ListChecks, testid: "nav-item-aufgaben" },
  { to: "/nutzer", label: "Nutzer", icon: UserCheck, testid: "nav-item-nutzer", roles: ["admin"] },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, testid: "nav-item-einstellungen", roles: ["admin", "manager"] },
];

const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;

export default function Layout() {
  const nav = useNavigate();
  const loc = useLocation();
  const user = getUser();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) { nav("/login", { replace: true }); return; }
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        clearAuth();
        nav("/login", { replace: true });
      }, IDLE_TIMEOUT_MS);
    };
    reset();
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset));
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
     
  }, []);

  if (!user) return null;

  const items = NAV.filter((it) => !it.roles || it.roles.includes(user.rolle));

  const doLogout = () => { clearAuth(); nav("/login", { replace: true }); };

  return (
    <div className="flex min-h-screen">
      <aside className="sidebar w-64 flex flex-col shrink-0" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Printer className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-semibold tracking-tight text-sm leading-tight">Die Druckexperten</div>
              <div className="text-slate-400 text-[11px] uppercase tracking-wider">CRM v0.1</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
            >
              <it.icon className="w-4 h-4" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 text-[11px] text-slate-500 border-t border-slate-800">
          © {new Date().getFullYear()} Die Druckexperten
        </div>
      </aside>
      <main className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 h-16 px-6 lg:px-8 flex items-center justify-between">
          <div className="text-sm text-slate-500 uppercase tracking-widest font-mono" data-testid="header-crumb">
            {loc.pathname.replace("/", "") || "Dashboard"}
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <button onClick={() => nav("/mein-profil")} className="text-right hidden sm:block group" data-testid="header-profile-button">
              <div className="text-sm font-medium text-slate-900 group-hover:text-blue-700" data-testid="header-user-name">
                {user.vorname} {user.nachname}
              </div>
              <div className="text-xs text-slate-500" data-testid="header-user-role">{ROLE_LABEL[user.rolle] || user.rolle}</div>
            </button>
            <button
              onClick={doLogout}
              data-testid="header-logout-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
          </div>
        </header>
        <div className="flex-1 p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
